import { useEffect, useRef, useState } from 'react';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 8;
const ZOOM_STEP = 0.25;
const WHEEL_ZOOM_STEP = 0.15;

function clampZoom(value: number): number {
  return parseFloat(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value)).toFixed(2));
}

export interface UseImageZoomResult {
  zoom: number;
  pan: { x: number; y: number };
  dragging: boolean;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
  onWheel: (e: React.WheelEvent) => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  stopDrag: () => void;
}

/**
 * Zoom/pan state for an image rendered INLINE within an existing preview
 * panel — not a full-screen takeover. Pass a `resetKey` (e.g. the doc id)
 * so zoom/pan resets whenever the displayed image changes.
 */
export function useImageZoom(resetKey?: string | null): UseImageZoomResult {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragOrigin = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  const reset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setDragging(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reset, [resetKey]);

  function zoomIn() {
    setZoom(z => clampZoom(z + ZOOM_STEP));
  }
  function zoomOut() {
    setZoom(z => clampZoom(z - ZOOM_STEP));
  }

  function onWheel(e: React.WheelEvent) {
    // Browsers report trackpad pinch-zoom and ctrl/cmd+wheel as wheel events
    // with ctrlKey (or metaKey on macOS) set. Only those gestures zoom; a
    // plain wheel/two-finger scroll is left alone so it can scroll/pan the
    // surrounding container instead of being hijacked into a zoom.
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? WHEEL_ZOOM_STEP : -WHEEL_ZOOM_STEP;
    setZoom(z => clampZoom(z + delta));
  }

  function onMouseDown(e: React.MouseEvent) {
    if (zoom <= 1) return;
    e.preventDefault();
    dragOrigin.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
    setDragging(true);
  }
  function onMouseMove(e: React.MouseEvent) {
    if (!dragging) return;
    setPan({
      x: dragOrigin.current.px + (e.clientX - dragOrigin.current.mx),
      y: dragOrigin.current.py + (e.clientY - dragOrigin.current.my),
    });
  }
  function stopDrag() {
    setDragging(false);
  }

  return { zoom, pan, dragging, zoomIn, zoomOut, reset, onWheel, onMouseDown, onMouseMove, stopDrag };
}
